/**
 * VisualCalendar Component
 * A visual monthly/weekly calendar view for displaying meetings
 */

import { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isToday, parseISO, addWeeks, subWeeks } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Grid3X3, List, Plus, Clock, Video, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Meeting } from '@/types/calendar.types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface VisualCalendarProps {
  meetings: Meeting[];
  onNewMeeting?: () => void;
  onMeetingClick?: (meeting: Meeting) => void;
  onJoinMeeting?: (meeting: Meeting) => void;
}

const TRANSLATIONS = {
  en: {
    monthView: 'Month',
    weekView: 'Week',
    today: 'Today',
    newMeeting: 'New Meeting',
    noMeetingsToday: 'No meetings',
    meetingsCount: 'meetings',
    join: 'Join',
    view: 'View',
    duration: 'Duration',
    minutes: 'min',
    participants: 'Participants',
    confirmed: 'Confirmed',
    pending: 'Pending',
    cancelled: 'Cancelled',
    completed: 'Completed',
    upcomingMeetings: 'Upcoming Meetings',
  },
  fr: {
    monthView: 'Mois',
    weekView: 'Semaine',
    today: "Aujourd'hui",
    newMeeting: 'Nouveau RDV',
    noMeetingsToday: 'Aucun RDV',
    meetingsCount: 'réunions',
    join: 'Rejoindre',
    view: 'Voir',
    duration: 'Durée',
    minutes: 'min',
    participants: 'Participants',
    confirmed: 'Confirmé',
    pending: 'En attente',
    cancelled: 'Annulé',
    completed: 'Terminé',
    upcomingMeetings: 'Prochains RDV',
  },
};

const STATUS_COLORS = {
  confirmed: 'bg-green-500',
  pending: 'bg-yellow-500',
  cancelled: 'bg-red-500',
  completed: 'bg-blue-500',
};

export function VisualCalendar({ 
  meetings, 
  onNewMeeting, 
  onMeetingClick,
  onJoinMeeting 
}: VisualCalendarProps) {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];
  const locale = language === 'fr' ? fr : enUS;
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  // Get days for current view
  const days = useMemo(() => {
    if (viewMode === 'month') {
      const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
  }, [currentDate, viewMode]);

  // Get meetings for a specific day
  const getMeetingsForDay = (day: Date) => {
    return meetings.filter(meeting => {
      const meetingDate = typeof meeting.date === 'string' 
        ? parseISO(meeting.date) 
        : meeting.date;
      return isSameDay(meetingDate, day);
    });
  };

  // Navigate functions
  const goToToday = () => setCurrentDate(new Date());
  const goPrevious = () => {
    setCurrentDate(viewMode === 'month' 
      ? subMonths(currentDate, 1) 
      : subWeeks(currentDate, 1)
    );
  };
  const goNext = () => {
    setCurrentDate(viewMode === 'month' 
      ? addMonths(currentDate, 1) 
      : addWeeks(currentDate, 1)
    );
  };

  const handleDayClick = (day: Date) => {
    const dayMeetings = getMeetingsForDay(day);
    if (dayMeetings.length > 0) {
      setSelectedDay(day);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed': return t.confirmed;
      case 'pending': return t.pending;
      case 'cancelled': return t.cancelled;
      case 'completed': return t.completed;
      default: return status;
    }
  };

  // Week days header
  const weekDays = useMemo(() => {
    const days = [];
    const start = startOfWeek(new Date(), { weekStartsOn: 1 });
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(format(day, 'EEE', { locale }));
    }
    return days;
  }, [locale]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goPrevious}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold min-w-[180px] text-center capitalize">
            {viewMode === 'month' 
              ? format(currentDate, 'MMMM yyyy', { locale })
              : `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM', { locale })} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM yyyy', { locale })}`
            }
          </h2>
          <Button variant="outline" size="icon" onClick={goNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday}>
            {t.today}
          </Button>
          
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'month' | 'week')}>
            <TabsList className="h-9">
              <TabsTrigger value="month" className="px-3">
                <Grid3X3 className="h-4 w-4 mr-1" />
                {t.monthView}
              </TabsTrigger>
              <TabsTrigger value="week" className="px-3">
                <List className="h-4 w-4 mr-1" />
                {t.weekView}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {onNewMeeting && (
            <Button size="sm" onClick={onNewMeeting}>
              <Plus className="h-4 w-4 mr-1" />
              {t.newMeeting}
            </Button>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {/* Week days header */}
          <div className="grid grid-cols-7 border-b">
            {weekDays.map((day, i) => (
              <div 
                key={i} 
                className="py-3 text-center text-sm font-medium text-muted-foreground capitalize"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className={cn(
            "grid grid-cols-7",
            viewMode === 'month' ? 'auto-rows-fr' : 'auto-rows-[180px]'
          )}>
            {days.map((day, index) => {
              const dayMeetings = getMeetingsForDay(day);
              const isCurrentMonth = viewMode === 'month' ? isSameMonth(day, currentDate) : true;
              const isSelected = selectedDay && isSameDay(day, selectedDay);
              
              return (
                <div
                  key={index}
                  className={cn(
                    "min-h-[100px] border-r border-b p-2 transition-colors cursor-pointer hover:bg-accent/50",
                    !isCurrentMonth && "bg-muted/30 text-muted-foreground",
                    isToday(day) && "bg-primary/5",
                    isSelected && "bg-primary/10 ring-2 ring-primary ring-inset"
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={cn(
                      "inline-flex items-center justify-center w-7 h-7 text-sm rounded-full",
                      isToday(day) && "bg-primary text-primary-foreground font-bold"
                    )}>
                      {format(day, 'd')}
                    </span>
                    {dayMeetings.length > 0 && viewMode === 'month' && (
                      <Badge variant="secondary" className="text-xs h-5">
                        {dayMeetings.length}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Meeting indicators */}
                  <div className="space-y-1">
                    {viewMode === 'month' ? (
                      // Month view: show dots/small badges
                      dayMeetings.slice(0, 3).map((meeting, i) => (
                        <TooltipProvider key={meeting.id}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className={cn(
                                  "text-xs px-1 py-0.5 rounded truncate",
                                  meeting.status === 'confirmed' && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                                  meeting.status === 'pending' && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
                                  meeting.status === 'cancelled' && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                                  meeting.status === 'completed' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedMeeting(meeting);
                                }}
                              >
                                {meeting.time} {meeting.title}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{meeting.title}</p>
                              <p className="text-xs">{meeting.time} • {meeting.duration} {t.minutes}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ))
                    ) : (
                      // Week view: show more details
                      dayMeetings.map((meeting) => (
                        <div
                          key={meeting.id}
                          className={cn(
                            "text-xs p-2 rounded border-l-4 cursor-pointer hover:opacity-80 transition-opacity",
                            meeting.status === 'confirmed' && "bg-green-50 border-l-green-500 dark:bg-green-900/20",
                            meeting.status === 'pending' && "bg-yellow-50 border-l-yellow-500 dark:bg-yellow-900/20",
                            meeting.status === 'cancelled' && "bg-red-50 border-l-red-500 dark:bg-red-900/20",
                            meeting.status === 'completed' && "bg-blue-50 border-l-blue-500 dark:bg-blue-900/20"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMeeting(meeting);
                          }}
                        >
                          <p className="font-medium truncate">{meeting.title}</p>
                          <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>{meeting.time}</span>
                            <span>•</span>
                            <span>{meeting.duration} {t.minutes}</span>
                          </div>
                          {meeting.meetingUrl && (
                            <div className="flex items-center gap-1 mt-1 text-primary">
                              <Video className="h-3 w-3" />
                              <span>Video</span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                    {dayMeetings.length > 3 && viewMode === 'month' && (
                      <p className="text-xs text-muted-foreground pl-1">
                        +{dayMeetings.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming meetings sidebar for month view */}
      {viewMode === 'month' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              {t.upcomingMeetings}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {meetings
                .filter(m => {
                  const meetingDate = typeof m.date === 'string' ? parseISO(m.date) : m.date;
                  return meetingDate >= new Date() && m.status !== 'cancelled' && m.status !== 'completed';
                })
                .slice(0, 5)
                .map((meeting) => (
                  <div 
                    key={meeting.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedMeeting(meeting)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        STATUS_COLORS[meeting.status as keyof typeof STATUS_COLORS]
                      )} />
                      <div>
                        <p className="font-medium">{meeting.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(typeof meeting.date === 'string' ? parseISO(meeting.date) : meeting.date, 'PPP', { locale })} • {meeting.time}
                        </p>
                      </div>
                    </div>
                    {meeting.meetingUrl && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onJoinMeeting?.(meeting);
                        }}
                      >
                        <Video className="h-4 w-4 mr-1" />
                        {t.join}
                      </Button>
                    )}
                  </div>
                ))
              }
              {meetings.filter(m => {
                const meetingDate = typeof m.date === 'string' ? parseISO(m.date) : m.date;
                return meetingDate >= new Date() && m.status !== 'cancelled';
              }).length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  {t.noMeetingsToday}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Meeting Details Dialog */}
      <Dialog open={!!selectedMeeting} onOpenChange={() => setSelectedMeeting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedMeeting?.title}</DialogTitle>
          </DialogHeader>
          {selectedMeeting && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={cn(
                  "text-white",
                  STATUS_COLORS[selectedMeeting.status as keyof typeof STATUS_COLORS]
                )}>
                  {getStatusLabel(selectedMeeting.status)}
                </Badge>
              </div>
              
              <div className="grid gap-3">
                <div className="flex items-center gap-3 text-sm">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {format(
                      typeof selectedMeeting.date === 'string' 
                        ? parseISO(selectedMeeting.date) 
                        : selectedMeeting.date, 
                      'PPP', 
                      { locale }
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedMeeting.time} • {selectedMeeting.duration} {t.minutes}</span>
                </div>
                {selectedMeeting.participants && selectedMeeting.participants.length > 0 && (
                  <div className="flex items-center gap-3 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedMeeting.participants.length} {t.participants}</span>
                  </div>
                )}
                {selectedMeeting.description && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {selectedMeeting.description}
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                {selectedMeeting.meetingUrl && selectedMeeting.status === 'confirmed' && (
                  <Button onClick={() => {
                    onJoinMeeting?.(selectedMeeting);
                    setSelectedMeeting(null);
                  }}>
                    <Video className="h-4 w-4 mr-2" />
                    {t.join}
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => {
                    onMeetingClick?.(selectedMeeting);
                    setSelectedMeeting(null);
                  }}
                >
                  {t.view}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Day Meetings Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={() => setSelectedDay(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedDay && format(selectedDay, 'PPPP', { locale })}
            </DialogTitle>
          </DialogHeader>
          {selectedDay && (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {getMeetingsForDay(selectedDay).map((meeting) => (
                <div 
                  key={meeting.id}
                  className={cn(
                    "p-3 rounded-lg border-l-4 cursor-pointer hover:bg-accent/50 transition-colors",
                    meeting.status === 'confirmed' && "border-l-green-500",
                    meeting.status === 'pending' && "border-l-yellow-500",
                    meeting.status === 'cancelled' && "border-l-red-500",
                    meeting.status === 'completed' && "border-l-blue-500"
                  )}
                  onClick={() => {
                    setSelectedDay(null);
                    setSelectedMeeting(meeting);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{meeting.title}</p>
                    <Badge variant="outline" className="text-xs">
                      {getStatusLabel(meeting.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{meeting.time}</span>
                    <span>•</span>
                    <span>{meeting.duration} {t.minutes}</span>
                  </div>
                  {meeting.meetingUrl && meeting.status === 'confirmed' && (
                    <Button 
                      size="sm" 
                      className="mt-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        onJoinMeeting?.(meeting);
                      }}
                    >
                      <Video className="h-4 w-4 mr-1" />
                      {t.join}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
